import { Table } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React from 'react';
import styles from './AuditHistory.module.scss';

dayjs.extend(utc);

export const ACTION_LABELS: Record<string, string> = {
    CREATE: 'Creación',
    UPDATE: 'Modificación',
    DELETE: 'Eliminación',
    ACTIVATE: 'Activación',
    DEACTIVATE: 'Desactivación',
    STATUS_CHANGE: 'Cambio de estado',
    IMPORT: 'Importación',
    SYNC: 'Sincronización',
    LINK: 'Vinculación',
    UNLINK: 'Desvinculación',
    UPLOAD: 'Carga de archivo',
    DELETE_FILE: 'Eliminación de archivo',
    PASSWORD_RESET: 'Cambio de contraseña',
};

export const MODULE_LABELS: Record<string, string> = {
    User: 'Usuario',
    Organization: 'Organización',
    Establishment: 'Establecimiento',
    EmissionSource: 'Fuente de emisión',
    ReferenceData: 'Datos de referencia',
    GeiInventory: 'Inventario GEI',
    ActivityData: 'Datos de actividad',
    Document: 'Documento',
    Event: 'Evento',
    Portfolio: 'Portafolio',
    Analytics: 'Analítica',
};

export const ENTITY_LABELS: Record<string, string> = {
    organization: 'Organización',
    establishment: 'Establecimiento',
    emission_sources: 'Fuente de emisión',
    emission_sources_organization: 'Fuente asignada a organización',
    transformation_factors: 'Factor de transformación',
    pcg: 'PCG',
    pcg_emission_sources: 'PCG y fuente',
    user: 'Usuario',
    organization_user: 'Usuario de organización',
    gei_inventory: 'Inventario GEI',
    establishment_gei_inventory: 'Establecimiento del inventario',
    activity_data: 'Dato de actividad',
    link_analytic: 'Enlace analítico',
    category: 'Categoría',
    source: 'Fuente',
    tag: 'Etiqueta',
    type_organization: 'Tipo de organización',
    type_event: 'Tipo de evento',
    emission_sources_type_organization: 'Fuente y tipo de organización',
    emission_sources_type_event: 'Fuente y tipo de evento',
    organization_parent: 'Organización padre',
    organization_parent_user: 'Usuario de organización padre',
    group_module: 'Módulo de grupo',
    module_permission: 'Permiso de módulo',
    permission: 'Permiso',
    event: 'Evento',
    schedule: 'Horario',
    event_activity_data: 'Dato de actividad del evento',
    gei_inventory_portafolio: 'Inventario GEI de portafolio',
    portfolio: 'Portafolio',
    amount_owed: 'Monto adeudado',
    portfolio_activity_data: 'Dato de actividad del portafolio',
    repository_activity_data: 'Documento de actividad',
};

export const SOURCE_LABELS: Record<string, string> = {
    identity: 'Identidad',
    gei: 'Inventario GEI',
    event: 'Eventos',
    portfolio: 'Portafolio',
};

const FIELD_LABELS: Record<string, string> = {
    description: 'Descripción',
    name: 'Nombre',
    status: 'Estado',
    url: 'URL',
    service: 'Servicio',
    fileName: 'Archivo',
    created: 'Creados',
    updated: 'Actualizados',
    ignored: 'Ignorados',
    closed: 'Cerrados',
    email: 'Correo',
    firstName: 'Nombre',
    lastName: 'Apellido',
    phone: 'Teléfono',
    year: 'Año',
    ruc: 'RUC',
    code: 'Código',
    month: 'Mes',
    previousFileName: 'Archivo anterior',
    activityDataId: 'Dato de actividad',
    moduleIds: 'Módulos',
    permissionIds: 'Permisos',
    count: 'Cantidad',
    parentOrganizationId: 'Organización padre',
    scheduleCount: 'Horarios',
    groupId: 'Grupo',
    method: 'Método',
};

const ACTION_COLORS: Record<string, string> = {
    CREATE: 'green',
    UPDATE: 'blue',
    DELETE: 'red',
    ACTIVATE: 'green',
    DEACTIVATE: 'orange',
    STATUS_CHANGE: 'gold',
    IMPORT: 'purple',
    SYNC: 'cyan',
    LINK: 'geekblue',
    UNLINK: 'volcano',
    UPLOAD: 'lime',
    DELETE_FILE: 'magenta',
    PASSWORD_RESET: 'default',
};

export function labelOf(map: Record<string, string>, value: string | null | undefined): string {
    if (!value) {
        return '—';
    }
    return map[value] ?? value;
}

export function actionColor(action: string): string {
    return ACTION_COLORS[action] ?? 'default';
}

export function fieldLabel(key: string): string {
    return FIELD_LABELS[key] ?? key.replace(/_/g, ' ');
}

export function formatOccurredAt(value: string | null | undefined): string {
    if (!value) {
        return '—';
    }
    const parsed = dayjs.utc(value);
    if (!parsed.isValid()) {
        return value;
    }
    return parsed.local().format('DD/MM/YYYY HH:mm');
}

export function toOptions(map: Record<string, string>): { value: string; label: string }[] {
    return Object.keys(map).map((value) => ({ value, label: map[value] }));
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isBlankOrUuid(value: string | undefined | null): boolean {
    if (value == null || value.trim() === '') {
        return true;
    }
    return UUID_PATTERN.test(value.trim());
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return value != null && typeof value === 'object' && !Array.isArray(value);
}

function isDiff(value: unknown): value is { old: unknown; new: unknown } {
    return isRecord(value) && Object.prototype.hasOwnProperty.call(value, 'old') && Object.prototype.hasOwnProperty.call(value, 'new');
}

function isScalar(value: unknown): boolean {
    return value == null || ['string', 'number', 'boolean'].includes(typeof value);
}

export function parseChanges(value: unknown): unknown {
    if (typeof value !== 'string') {
        return value;
    }
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
}

export function formatChangeValue(value: unknown): string {
    if (value == null || value === '') {
        return '—';
    }
    if (typeof value === 'boolean') {
        return value ? 'Sí' : 'No';
    }
    if (typeof value === 'number') {
        return String(value);
    }
    if (typeof value === 'string') {
        return value;
    }
    try {
        return JSON.stringify(value);
    } catch {
        return '—';
    }
}

function safeJson(value: unknown): string {
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

function KeyValueTable(props: { rows: { key: string; label: string; value: string }[] }) {
    return (
        <Table
            size="small"
            pagination={false}
            rowKey="key"
            dataSource={props.rows}
            columns={[
                { title: 'Campo', dataIndex: 'label', width: '40%' },
                { title: 'Valor', dataIndex: 'value', render: (text: string) => <span className={styles.valueText}>{text}</span> },
            ]}
        />
    );
}

export const ChangesView: React.FC<{ changes: unknown }> = ({ changes }) => {
    const parsed = parseChanges(changes);
    if (!isRecord(parsed)) {
        return <pre className={styles.jsonBlock}>{safeJson(parsed)}</pre>;
    }

    const keys = Object.keys(parsed);
    if (keys.length === 0) {
        return <p className={styles.muted}>No hay cambios detallados.</p>;
    }

    const snapshot = Object.prototype.hasOwnProperty.call(parsed, 'snapshot') ? parsed.snapshot : undefined;
    const diffRows: { key: string; field: string; previous: string; next: string }[] = [];
    const summaryRows: { key: string; label: string; value: string }[] = [];
    const unknown: Record<string, unknown> = {};

    Object.keys(parsed).forEach((key) => {
        if (key === 'snapshot') {
            return;
        }
        const value = parsed[key];
        if (isDiff(value)) {
            diffRows.push({
                key,
                field: fieldLabel(key),
                previous: formatChangeValue(value.old),
                next: formatChangeValue(value.new),
            });
            return;
        }
        if (isScalar(value)) {
            summaryRows.push({ key, label: fieldLabel(key), value: formatChangeValue(value) });
            return;
        }
        unknown[key] = value;
    });

    const snapshotRows = isRecord(snapshot)
        ? Object.keys(snapshot).map((key) => ({
            key,
            label: fieldLabel(key),
            value: formatChangeValue(snapshot[key]),
        }))
        : [];

    return (
        <div className={styles.changes}>
            {snapshot !== undefined && (
                <section>
                    <h3>Datos eliminados</h3>
                    {isRecord(snapshot)
                        ? <KeyValueTable rows={snapshotRows} />
                        : <pre className={styles.jsonBlock}>{safeJson(snapshot)}</pre>}
                </section>
            )}
            {diffRows.length > 0 && (
                <section>
                    <h3>Cambios</h3>
                    <Table
                        size="small"
                        pagination={false}
                        rowKey="key"
                        dataSource={diffRows}
                        columns={[
                            { title: 'Campo', dataIndex: 'field', width: '28%' },
                            { title: 'Valor anterior', dataIndex: 'previous', render: (text: string) => <span className={styles.valueText}>{text}</span> },
                            { title: 'Valor nuevo', dataIndex: 'next', render: (text: string) => <span className={styles.valueText}>{text}</span> },
                        ]}
                    />
                </section>
            )}
            {summaryRows.length > 0 && (
                <section>
                    <h3>Resumen</h3>
                    <KeyValueTable rows={summaryRows} />
                </section>
            )}
            {Object.keys(unknown).length > 0 && (
                <section>
                    <h3>Detalle adicional</h3>
                    <pre className={styles.jsonBlock}>{safeJson(unknown)}</pre>
                </section>
            )}
        </div>
    );
};
